import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Copy, Sparkles, Users, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadText, tsFilename } from "@/lib/download";
import WaveformBars from "@/components/Waveform.jsx";

const STAGES = {
  idle: "Ready",
  recording: "Recording meeting…",
  uploading: "Uploading…",
  transcribing: "Transcribing…",
  polishing: "Building notes…",
  done: "Notes ready",
};

export default function NotesTakerPage() {
  const [stage, setStage] = useState("idle");
  const [rawText, setRawText] = useState("");
  const [notes, setNotes] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [mode, setMode] = useState("meeting_note"); // "meeting_note" → English notes, "raw" → raw transcript
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const recording = stage === "recording";
  const busy = ["uploading", "transcribing", "polishing"].includes(stage);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 48000, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 96000 });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        await new Promise((r) => setTimeout(r, 300));
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        await transcribe(blob);
      };
      mr.start(500);
      mediaRecorderRef.current = mr;
      setStage("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone permission denied.");
    }
  };

  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setStage("uploading");
    try { mediaRecorderRef.current?.stop(); } catch { /* swallow */ }
  };

  const transcribe = async (blob) => {
    setStage("transcribing");
    try {
      const form = new FormData();
      form.append("audio", blob, "meeting.webm");
      form.append("language", "auto");
      const { data } = await api.post("/voice/transcribe", form, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
      });
      const text = (data.text || "").trim();
      if (!text) { toast.error("No speech detected."); setStage("idle"); return; }
      setRawText(text);
      if (mode === "raw") {
        setNotes(text);
        await api.post("/ai/polish", { raw_text: text, output_mode: "raw", language: "auto", save_history: true }).catch(() => {});
        setStage("done");
      } else {
        await polish(text);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transcription failed.");
      setStage("idle");
    }
  };

  const polish = async (text) => {
    setStage("polishing");
    try {
      const { data } = await api.post("/ai/polish", {
        raw_text: text || rawText, output_mode: mode === "raw" ? "raw" : "meeting_note", language: "auto", save_history: true,
      });
      setNotes(data.polished_text);
      setStage("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build notes.");
      setStage("idle");
    }
  };

  const copy = async (t) => { await navigator.clipboard.writeText(t); toast.success("Copied"); };
  const downloadCombined = () => {
    const parts = [];
    if (rawText) parts.push(`# Meeting transcript\n\n${rawText}`);
    if (notes && notes !== rawText) parts.push(`\n\n# Meeting notes (summary · key points · action items)\n\n${notes}`);
    downloadText(tsFilename("draftvoice-meeting"), parts.join("\n") || "");
  };
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="px-5 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-gradient-secondary flex items-center justify-center text-white"><Users className="w-5 h-5" /></div>
        <div>
          <h1 className="text-3xl font-bold text-brand-navy">Notes Taker</h1>
          <p className="text-sm text-brand-muted mt-1">Record a meeting (any length, English/Hindi/Telugu/Mixed) → get transcript, summary, decisions, action items and a follow-up email.</p>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="bg-white rounded-2xl border border-brand-borderc p-4 mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Output</div>
          <div className="text-sm text-brand-text mt-0.5">
            Default: <strong>English meeting notes</strong> · switch to Raw Transcript to keep the spoken language.
          </div>
        </div>
        <div className="inline-flex items-center gap-1 bg-brand-bg border border-brand-borderc rounded-full p-1">
          <button
            data-testid="notes-mode-meeting"
            onClick={() => setMode("meeting_note")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold ${mode === "meeting_note" ? "bg-gradient-primary text-white" : "text-brand-muted"}`}
          >
            English notes
          </button>
          <button
            data-testid="notes-mode-raw"
            onClick={() => setMode("raw")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold ${mode === "raw" ? "bg-gradient-primary text-white" : "text-brand-muted"}`}
          >
            Raw Transcript
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-brand-borderc p-8 shadow-soft relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh opacity-50 pointer-events-none" />
        <div className="relative flex flex-col items-center">
          <div className="text-xs uppercase tracking-widest font-semibold text-brand-muted">{STAGES[stage]}</div>
          <div className="text-3xl font-mono font-bold text-brand-navy mt-1">{fmt(seconds)}</div>
          <div className="relative my-6">
            {recording && <span className="absolute inset-0 rounded-full bg-brand-purple/30 animate-pulse-ring" />}
            <button
              data-testid="notes-mic-toggle"
              onClick={recording ? stop : start} disabled={busy}
              className={`relative w-24 h-24 rounded-full flex items-center justify-center shadow-glow-purple transition ${
                recording ? "bg-gradient-warm" : "btn-primary-gradient"
              } disabled:opacity-60`}
            >
              {busy ? <Loader2 className="w-10 h-10 text-white animate-spin" /> : recording ? <Square className="w-9 h-9 text-white" /> : <Mic className="w-10 h-10 text-white" />}
            </button>
          </div>
          <WaveformBars active={recording || stage === "transcribing" || stage === "polishing"} count={28} />
          <p className="mt-3 text-sm text-brand-muted">{recording ? "Tap to stop and build notes" : "Tap to start. You can record for up to ~25 minutes."}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="bg-white rounded-2xl border border-brand-borderc p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-brand-navy">Full transcript</h3>
            <div className="flex gap-2">
              <button onClick={() => copy(rawText)} disabled={!rawText} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface hover:bg-brand-borderc disabled:opacity-40 inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy</button>
              <button data-testid="notes-download-transcript" onClick={() => downloadText(tsFilename("draftvoice-transcript"), rawText)} disabled={!rawText} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface hover:bg-brand-borderc disabled:opacity-40 inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> .txt</button>
            </div>
          </div>
          <textarea data-testid="notes-transcript" value={rawText} onChange={(e) => setRawText(e.target.value)} rows={14}
            placeholder="The full meeting transcript appears here in the spoken language."
            className="mt-3 w-full p-3 rounded-xl border border-brand-borderc bg-brand-bg text-sm focus:border-brand-purple outline-none resize-none" />
          {rawText && !notes && !busy && (
            <button onClick={() => polish()} className="mt-3 btn-primary-gradient rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Build notes
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-brand-borderc p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-brand-navy">Meeting notes</h3>
            <div className="flex gap-2">
              <button data-testid="notes-copy" onClick={() => copy(notes)} disabled={!notes} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface hover:bg-brand-borderc disabled:opacity-40 inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy all</button>
              <button data-testid="notes-download" onClick={downloadCombined} disabled={!notes && !rawText} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface hover:bg-brand-borderc disabled:opacity-40 inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Download .txt</button>
            </div>
          </div>
          <textarea data-testid="notes-output" value={notes} onChange={(e) => setNotes(e.target.value)} rows={14}
            placeholder="Summary, key points, decisions, action items and a follow-up email will appear here once you stop recording."
            className="mt-3 w-full p-3 rounded-xl border border-brand-borderc bg-white text-sm focus:border-brand-purple outline-none resize-none whitespace-pre-wrap" />
        </div>
      </div>

      <div className="mt-5 rounded-xl bg-brand-surface text-brand-text px-4 py-3 text-xs flex gap-2 items-start">
        <AlertCircle className="w-4 h-4 text-brand-purple shrink-0 mt-0.5" />
        <span>Notes Taker uses Auto language + Meeting Notes mode. The full transcript and the structured notes are both saved to your History.</span>
      </div>
    </div>
  );
}
